import type React from "react";
import { useState } from "react";
import { Maximize2 } from "lucide-react";
import { fmtInt } from "@/lib/format";
import { Dialog, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { EmptyState } from "@/components/EmptyState";
import { ErrorCard } from "@/components/ErrorCard";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { KPIGrid, type KPITile } from "../dashboard/KPIGrid";
import { useEmergencyMap, type SelectedView } from "@/features/periods/usePeriods";
import { EmergencyMapChart } from "@/features/emergency-map/EmergencyMapChart";
import type { emergencyMap } from "@duress/shared";

function buildTiles(data: emergencyMap.EmergencyMapResponse): KPITile[] {
  const mapped = data.totalEvents - data.unmappedEvents;
  const pendingTile: KPITile =
    data.pendingGeocodeCount > 0
      ? { label: "Pending", value: fmtInt(data.pendingGeocodeCount), sub: "resolving location…" }
      : { label: "Pending", value: fmtInt(data.pendingGeocodeCount) };
  return [
    { label: "Total Events", value: fmtInt(data.totalEvents) },
    { label: "Mapped", value: fmtInt(mapped), sub: "have a usable address" },
    pendingTile,
  ];
}

export function EmergencyMap({ data }: { data: emergencyMap.EmergencyMapResponse }): React.JSX.Element {
  const [expanded, setExpanded] = useState(false);

  if (data.totalEvents === 0) {
    return (
      <EmptyState body="No emergency events in this window." />
    );
  }

  if (data.unmappedEvents === data.totalEvents) {
    return (
      <EmptyState
        title="No location data for this client"
        body="This client's emergency export doesn't include an address or location column, so events can't be mapped."
      />
    );
  }

  return (
    <div className="space-y-6">
      <KPIGrid tiles={buildTiles(data)} />

      <div className="card-duress p-5">
        <div className="mb-4 flex items-center justify-between">
          <p className="font-head font-semibold text-sm text-foreground">Emergency Map</p>
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="flex items-center gap-1 text-xs font-medium text-phoenix-orange hover:underline"
          >
            <Maximize2 className="h-3.5 w-3.5" />
            Expand
          </button>
        </div>
        <EmergencyMapChart points={data.points} />
      </div>

      <Dialog open={expanded} onOpenChange={setExpanded} className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Emergency Map</DialogTitle>
          <DialogDescription>
            Drag to pan, scroll or use the buttons to zoom. Point size reflects event count at that
            location.
          </DialogDescription>
        </DialogHeader>
        <EmergencyMapChart points={data.points} interactive />
      </Dialog>
    </div>
  );
}

export function EmergencyMapTab({
  clientId,
  view,
}: {
  clientId: string;
  view: SelectedView | null;
}): React.JSX.Element {
  const { data, isLoading, error, refetch } = useEmergencyMap(clientId, view);

  if (!view) {
    return <EmptyState body="Select a reporting period above." />;
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <LoadingSkeleton variant="kpis" />
        <LoadingSkeleton variant="chart" />
      </div>
    );
  }

  if (error || !data) {
    return <ErrorCard message="Failed to load emergency map data." onRetry={refetch} />;
  }

  return <EmergencyMap data={data} />;
}
